import React, { useState } from 'react';
import { FormField } from '@/components/FormField';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { BaseAdSetForm } from './BaseAdSetForm';
import { AttributionWindowSection, buildAttributionSpec } from './AttributionWindowSection';
import { ODAX_MATRIX, GOAL_LABEL } from './odaxMatrix';
import { FbAppPicker } from '@/components/facebook/MetaResourcePickers';
import type { MetaApp } from '@/services/meta-resources.service';
import { useFbWorkspace } from '@/contexts/FbWorkspaceContext';

// Single-cell objective: App location with a fixed goal set.
const APP_PROMO_LOCATION = ODAX_MATRIX.OUTCOME_APP_PROMOTION[0];
const OPT_GOALS = APP_PROMO_LOCATION.goals.map((g) => ({
  code: g,
  label: GOAL_LABEL[g] ?? g,
}));

export const AppPromotionsAdSetForm: React.FC = () => {
  const { appPromotion } = useFbWorkspace();
  const [manualAppId, setAppId] = useState('');
  const appId = appPromotion?.metaAppId ?? manualAppId;
  const [picked, setPicked] = useState<MetaApp | null>(null);
  // Which store URL Meta should optimize for. App Promotion ad sets can target
  // iOS or Android — the store URL Meta uses must match the platform.
  const [storeChoice, setStoreChoice] = useState<'iphone_app_store' | 'google_play' | 'amazon_app_store'>(appPromotion?.platform === 'ios' ? 'iphone_app_store' : 'google_play');
  // Attribution windows — Meta default for APP_PROMOTION is 7d click + 1d view.
  // Meta only accepts (1d click, no view) or (7d click, no view) for app
  // goals — the old 7d+1d-view default was rejected live (2026-08-28):
  // "supported combination of click-through and view-through attribution
  // window values are: (1, 0)". Default to the universally-valid pair.
  const [clickWin, setClickWin] = useState('1');
  const [viewWin, setViewWin] = useState('0');

  const storeUrl = appPromotion?.storeUrl ?? picked?.objectStoreUrls?.[storeChoice] ?? '';
  const availableStores = picked
    ? Object.entries(picked.objectStoreUrls ?? {}).filter(([, v]) => !!v).map(([k]) => k as typeof storeChoice)
    : [];

  const extraPayload = () => ({
    promoted_object: appId
      ? { application_id: appId, object_store_url: storeUrl }
      : undefined,
    attribution_spec: buildAttributionSpec(clickWin, viewWin),
  });

  // Meta requires the ad set's `user_os` AND `device_platforms` to match the
  // platform of the chosen app store URL. Default `device_platforms` is
  // unset = both mobile + desktop, which conflicts with mobile-only apps
  // and triggers:
  //   "The app you're trying to create an ad for is on a different operating
  //    system than targeting settings for this ad set."
  // Amazon Appstore is Android, so it maps to Android too.
  const extraTargeting = () => {
    const os =
      storeChoice === 'iphone_app_store' ? ['iOS'] :
      (storeChoice === 'google_play' || storeChoice === 'amazon_app_store') ? ['Android'] :
      undefined;
    return os
      ? { user_os: os, device_platforms: ['mobile'] }
      : { device_platforms: ['mobile'] };
  };

  const validateExtra = () => {
    if (!appId) return 'Pick a Facebook App';
    if (!storeUrl) return 'This app has no app-store URL set on developers.facebook.com — add one in Settings → Basic';
    return null;
  };

  return (
    <BaseAdSetForm
      optimizationGoals={OPT_GOALS}
      extraPayload={extraPayload}
      extraTargeting={extraTargeting}
      validateExtra={validateExtra}
      expectedObjective="OUTCOME_APP_PROMOTION"
    >
      {appPromotion ? <div className="rounded-xl border border-border bg-primary-soft/40 p-4 text-sm">
        <p className="font-semibold text-text-primary">{appPromotion.name} · {appPromotion.platform === 'ios' ? 'iOS' : 'Android'}</p>
        <p className="mt-1 text-xs text-text-muted">Meta app ID: {appPromotion.metaAppId}</p>
        <p className="mt-2 break-all text-xs text-text-secondary">{storeUrl}</p>
        <p className="mt-2 text-xs text-text-muted">Install attribution/tracking is not configured in this version. Meta may require app events or additional app permissions for your selected goal.</p>
      </div> : <FormField
        label="Mobile App"
        required
        hintBelow
        hint="Pick from apps you've registered at developers.facebook.com."
      >
        <FbAppPicker
          value={appId}
          mobileOnly
          onChange={(id, app) => {
            setAppId(id);
            setPicked(app);
            // Default to whichever store has a URL set (prefer Android since it's more common in MENA).
            const stores = app?.objectStoreUrls ?? {};
            if (stores.google_play) setStoreChoice('google_play');
            else if (stores.iphone_app_store) setStoreChoice('iphone_app_store');
            else if (stores.amazon_app_store) setStoreChoice('amazon_app_store');
          }}
        />
      </FormField>}

      {picked && (
        <FormField
          label="Optimize for store"
          hintBelow
          hint="Each ad set targets one store. To run on both iOS and Android, save this ad set then duplicate it from the sidebar and switch the store."
        >
          <Select value={storeChoice} onChange={e => setStoreChoice(e.target.value as typeof storeChoice)}>
            <option value="google_play" disabled={!picked.objectStoreUrls?.google_play}>
              Google Play (Android){picked.objectStoreUrls?.google_play ? '' : ' — not configured'}
            </option>
            <option value="iphone_app_store" disabled={!picked.objectStoreUrls?.iphone_app_store}>
              App Store (iOS){picked.objectStoreUrls?.iphone_app_store ? '' : ' — not configured'}
            </option>
            <option value="amazon_app_store" disabled={!picked.objectStoreUrls?.amazon_app_store}>
              Amazon Appstore{picked.objectStoreUrls?.amazon_app_store ? '' : ' — not configured'}
            </option>
          </Select>
          {availableStores.length < 2 && (
            <p className="mt-1.5 text-xs text-text-muted">
              Missing platforms? Add their store URLs at{' '}
              <a
                href="https://developers.facebook.com/apps/"
                target="_blank"
                rel="noreferrer"
                className="text-primary hover:underline"
              >
                developers.facebook.com → Settings → Basic
              </a>.
            </p>
          )}
        </FormField>
      )}

      {picked && storeUrl && (
        <FormField label="App store URL">
          <Input value={storeUrl} disabled className="font-mono text-xs" />
        </FormField>
      )}

      <AttributionWindowSection
        clickWin={clickWin}
        setClickWin={setClickWin}
        viewWin={viewWin}
        setViewWin={setViewWin}
        clickOptions={['1', '7']}
        hideViewThrough
        hint="App campaigns accept a 1- or 7-day click window with no view-through — Meta rejects other combinations."
      />
    </BaseAdSetForm>
  );
};
